import { useCallback, useEffect, useState } from "react";
import { apiGet, apiPost } from "./api";
import type { ActionCard } from "../types/cards";

interface CardsResponse {
  cards: ActionCard[];
}

interface CardResponse {
  card: ActionCard | null;
}

// Action cards for the caller's parish: pending plans + applied
// receipts. One fetch on mount; mutations (apply / retry / dismiss)
// and chat-born cards update local state from the server's response
// rather than refetching the list.
export function useActionCards(enabled = true) {
  const [cards, setCards] = useState<ActionCard[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    apiGet<CardsResponse>("/cards")
      .then((data) => {
        if (cancelled) return;
        setCards(data.cards);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const replaceCard = useCallback((card: ActionCard) => {
    setCards((prev) => prev.map((c) => (c.id === card.id ? card : c)));
  }, []);

  // A card just arrived over the chat stream — put it first, and drop
  // the pending card it superseded (chat refinement), if any.
  const upsertCard = useCallback(
    (card: ActionCard, replacedCardId: string | null) => {
      setCards((prev) => [
        card,
        ...prev.filter((c) => c.id !== card.id && c.id !== replacedCardId),
      ]);
    },
    [],
  );

  const applyCard = useCallback(
    async (cardId: string, lineIds: string[]): Promise<ActionCard | null> => {
      const res = await apiPost<CardResponse>(`/cards/${cardId}/apply`, { lineIds });
      if (res.card) replaceCard(res.card);
      return res.card;
    },
    [replaceCard],
  );

  const retryLine = useCallback(
    async (cardId: string, lineId: string): Promise<ActionCard | null> => {
      const res = await apiPost<CardResponse>(`/cards/${cardId}/lines/${lineId}/retry`, {});
      if (res.card) replaceCard(res.card);
      return res.card;
    },
    [replaceCard],
  );

  const dismissCard = useCallback(async (cardId: string): Promise<void> => {
    await apiPost<CardResponse>(`/cards/${cardId}/dismiss`, {});
    setCards((prev) => prev.filter((c) => c.id !== cardId));
  }, []);

  return { cards, loading, error, upsertCard, applyCard, retryLine, dismissCard };
}
